import React, { useEffect, useState } from "react";
import { Sparkles, Send, RefreshCw, Play } from "lucide-react";
import { crmApi } from "../../lib/crmApi";

const card = "border border-[#6E1F2C]/15 bg-white p-3";
const lbl = "text-[9px] tracking-[0.16em] uppercase text-[#7A6B5F]";
const inputCls = "w-full border border-[#6E1F2C]/25 bg-white px-2.5 py-1.5 text-[12px] text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]";
const btn = "px-3 py-1.5 text-[10px] tracking-[0.14em] uppercase border border-[#6E1F2C]/40 text-[#6E1F2C] hover:bg-[#6E1F2C]/5 disabled:opacity-40 inline-flex items-center gap-1.5";
const btnSolid = "px-3 py-1.5 text-[10px] tracking-[0.14em] uppercase bg-[#6E1F2C] text-white disabled:opacity-40 inline-flex items-center gap-1.5";

const EVENT_COLORS = { message: "#3E2A22", stage_change: "#A6822F", ai_run: "#2C5F6E", appointment: "#3F6E2C", concept: "#6E1F2C" };

function ScoreRow({ scores }) {
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(scores || {}).map(([k, v]) => (
        <span key={k} className={`text-[9px] px-1.5 py-0.5 border ${v >= 70 ? "text-[#3F6E2C] border-[#3F6E2C]/40" : v >= 40 ? "text-[#A6822F] border-[#A6822F]/40" : "text-[#A3141A] border-[#A3141A]/40"}`}>
          {k.replace(/_/g, " ")}: {v}
        </span>))}
    </div>
  );
}

export function ReplayTab() {
  const [leads, setLeads] = useState([]);
  const [sel, setSel] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fService, setFService] = useState("");
  const [fStaff, setFStaff] = useState("");
  useEffect(() => { crmApi.leads({ limit: 200 }).then((r) => setLeads(r.leads || r.items || r || [])).catch(() => {}); }, []);
  const services = [...new Set(leads.map((l) => l.service_category).filter(Boolean))];
  const staff = [...new Set(leads.map((l) => l.assigned_salesperson_name || l.assigned_salesperson).filter(Boolean))];
  const filtered = leads.filter((l) =>
    (!fStatus || (fStatus === "pending" ? !["won", "lost"].includes(l.lead_status) : l.lead_status === fStatus))
    && (!fService || l.service_category === fService)
    && (!fStaff || (l.assigned_salesperson_name || l.assigned_salesperson) === fStaff));
  const load = (id) => {
    setSel(id); setD(null); setMsg("");
    if (id) crmApi.insightsReplay(id).then(setD).catch((e) => setMsg(e.message));
  };
  const reviewNow = async () => {
    setBusy(true);
    try { await crmApi.insightsReviewNow(sel); load(sel); } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="insights-replay">
      <div className="flex gap-2 items-center flex-wrap">
        <select className={`${inputCls} max-w-[120px]`} value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="replay-filter-status">
          <option value="">All outcomes</option>
          <option value="won">Won</option>
          <option value="lost">Lost</option>
          <option value="pending">Pending</option>
        </select>
        <select className={`${inputCls} max-w-[150px]`} value={fService} onChange={(e) => setFService(e.target.value)} data-testid="replay-filter-service">
          <option value="">All services</option>
          {services.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select className={`${inputCls} max-w-[150px]`} value={fStaff} onChange={(e) => setFStaff(e.target.value)} data-testid="replay-filter-staff">
          <option value="">All salespeople</option>
          {staff.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={`${inputCls} max-w-xs`} data-testid="replay-lead-select" value={sel} onChange={(e) => load(e.target.value)}>
          <option value="">Select a conversation to replay…</option>
          {filtered.map((l) => <option key={l.id} value={l.id}>{`${l.lead_ref} — ${l.customer_name || l.whatsapp_number} (${l.lead_status})`}</option>)}
        </select>
        {sel && <button type="button" className={btn} data-testid="replay-review-now" onClick={reviewNow} disabled={busy}>
          <Sparkles size={11} /> {busy ? "Scoring…" : "AI review now"}</button>}
      </div>
      {msg && <p className="text-[11px] text-[#A3141A]">{msg}</p>}
      {d && (
        <>
          <div className={card}>
            <p className="text-[12px] text-[#1C110C] font-medium">{d.lead?.lead_ref} · {d.lead?.customer_name} — outcome: <span className="uppercase text-[#6E1F2C]">{d.outcome}</span></p>
            {(d.reviews || []).map((r) => (
              <div key={r.id} className="mt-2 space-y-1" data-testid="replay-review">
                <ScoreRow scores={r.scores} />
                <p className="text-[10px] text-[#7A6B5F]">Takeover timing: {r.human_takeover_timing} · {r.summary}</p>
                {(r.missed_opportunities || []).length > 0 && (
                  <p className="text-[10px] text-[#A6822F]">Missed: {r.missed_opportunities.join("; ")}</p>)}
              </div>))}
            {(d.reviews || []).length === 0 && <p className="text-[10px] text-[#7A6B5F] italic mt-1">Not scored yet — click "AI review now".</p>}
          </div>
          <div className={`${card} max-h-[480px] overflow-y-auto space-y-1.5`} data-testid="replay-timeline">
            {(d.events || []).map((e, i) => (
              <div key={i} className="flex gap-2 text-[11px]">
                <span className="text-[#7A6B5F] shrink-0 w-24">{(e.t || "").slice(5, 16).replace("T", " ")}</span>
                <span className="shrink-0 w-24 uppercase text-[9px] pt-0.5" style={{ color: EVENT_COLORS[e.type] }}>
                  {e.type === "message" ? e.data.sender_type : e.type.replace(/_/g, " ")}</span>
                <span className="text-[#3E2A22]">
                  {e.type === "message" && `${e.data.message_type !== "text" ? `[${e.data.message_type}] ` : ""}${(e.data.message_text || "").slice(0, 220)}`}
                  {e.type === "stage_change" && `${e.data.from || "new"} → ${e.data.to}`}
                  {e.type === "ai_run" && `AI run · ${Math.round((e.data.total_ms || 0) / 1000)}s${e.data.images ? ` · ${e.data.images} image(s)` : ""}${(e.data.kb_used || []).length ? ` · knowledge: ${e.data.kb_used.join(", ")}` : ""}`}
                  {e.type === "appointment" && `${e.data.appt_type || "visit"} ${e.data.start?.slice(0, 16)} (${e.data.status})`}
                  {e.type === "concept" && `Concept image — ${e.data.status}${e.data.approved_by ? ` · approved by ${e.data.approved_by}` : ""}`}
                </span>
              </div>))}
          </div>
        </>
      )}
    </div>
  );
}

export function LearningTab() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => crmApi.insightsLearning().then(setD).catch((e) => setMsg(e.message));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const summarise = async () => {
    setBusy(true); setMsg("");
    try { await crmApi.insightsLearningSummarise(); load(); } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  const c = d?.counts || {};
  return (
    <div className="space-y-3" data-testid="insights-learning">
      <p className="text-[11px] text-[#7A6B5F]">Every time staff edit, rewrite or accept an AI draft (replies & image prompts) it is recorded here. The AI turns these into suggested style rules — nothing changes automatically; an admin decides.</p>
      <div className="flex gap-2 items-center flex-wrap">
        <span className="text-[11px] text-[#3E2A22]" data-testid="learning-counts">
          Accepted: {c.accepted || 0} · Edited: {c.edited || 0} · Rewritten: {c.rewritten || 0}</span>
        <button type="button" className={btnSolid} data-testid="learning-summarise-btn" onClick={summarise} disabled={busy}>
          <Sparkles size={11} /> {busy ? "Analysing…" : "Analyse edits → suggestions"}</button>
      </div>
      {msg && <p className="text-[11px] text-[#A3141A]">{msg}</p>}
      {d?.summary && (
        <div className={card} data-testid="learning-summary">
          <p className={`${lbl} mb-1`}>Patterns ({d.summary.samples_analysed} samples · {d.summary.created_at?.slice(0, 10)})</p>
          <ul className="list-disc ml-4 text-[11px] text-[#3E2A22] space-y-0.5">
            {(d.summary.patterns || []).map((p, i) => <li key={i}>{p}</li>)}
          </ul>
          <p className={`${lbl} mt-2 mb-1`}>Suggested style rules (copy into AI Settings if you agree)</p>
          <ul className="list-disc ml-4 text-[11px] text-[#6E1F2C] space-y-0.5">
            {(d.summary.suggested_style_rules || []).map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>)}
      <div className={`${card} max-h-[360px] overflow-y-auto space-y-2`}>
        {(d?.feedback || []).map((f) => (
          <div key={f.id} className="text-[10px] border-b border-[#6E1F2C]/8 pb-1.5">
            <p className="text-[#7A6B5F]">{f.kind} · <span className={f.verdict === "accepted" ? "text-[#3F6E2C]" : f.verdict === "edited" ? "text-[#A6822F]" : "text-[#A3141A]"}>{f.verdict}</span> · similarity {Math.round((f.similarity || 0) * 100)}% · {f.staff_name} · {f.created_at?.slice(0, 16).replace("T", " ")}</p>
            {f.verdict !== "accepted" && (
              <>
                <p className="text-[#7A6B5F] line-clamp-1">AI: {f.original}</p>
                <p className="text-[#3E2A22] line-clamp-1">Staff: {f.final}</p>
              </>)}
          </div>))}
        {(d?.feedback || []).length === 0 && <p className="text-[11px] text-[#7A6B5F] italic">No staff edits recorded yet.</p>}
      </div>
    </div>
  );
}

export function BriefingsTab() {
  const [brief, setBrief] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => {
    crmApi.insightsBriefing().then((r) => setBrief(r.briefing)).catch(() => {});
    crmApi.insightsWeekly().then((r) => setWeekly(r.review)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const regen = async (which) => {
    setBusy(which);
    try {
      if (which === "daily") setBrief((await crmApi.insightsBriefing(true)).briefing);
      else setWeekly((await crmApi.insightsWeekly(true)).review);
    } catch (e) { /* noop */ }
    setBusy("");
  };
  const b = brief?.data || {};
  return (
    <div className="grid xl:grid-cols-2 gap-3" data-testid="insights-briefings">
      <div className={card} data-testid="daily-briefing">
        <div className="flex justify-between items-center mb-2">
          <p className={lbl}>Daily briefing — {brief?.date || "not generated yet"} (auto at 08:00 Dubai)</p>
          <button type="button" className={btn} onClick={() => regen("daily")} disabled={!!busy} data-testid="briefing-regen">
            <RefreshCw size={10} /> {busy === "daily" ? "…" : "Regenerate"}</button>
        </div>
        {brief && (
          <div className="text-[11px] text-[#3E2A22] space-y-1.5">
            <p>New leads: {b.new_leads_today} today · {b.new_leads_yesterday} yesterday · Follow-ups due: {b.followups_due} · Quotes due: {b.quotes_due}</p>
            <p>Customers awaiting reply: <b>{b.customers_awaiting_reply ?? "—"}</b> · Waiting &gt;24h: <b className="text-[#A3141A]">{b.leads_waiting_over_24h ?? "—"}</b></p>
            <p>Pipeline: {b.pipeline?.n || 0} open · AED {(b.pipeline?.vmin || 0).toLocaleString()}–{(b.pipeline?.vmax || 0).toLocaleString()}</p>
            {(b.urgent_hot || []).length > 0 && <p className="text-[#A3141A]">Urgent hot: {b.urgent_hot.map((l) => l.lead_ref).join(", ")}</p>}
            {(b.leads_at_risk || []).length > 0 && <p className="text-[#A6822F]">At risk: {b.leads_at_risk.map((l) => l.lead_ref).join(", ")}</p>}
            {(b.appointments_today || []).length > 0 && (
              <p>Today: {b.appointments_today.map((a) => `${a.start?.slice(11, 16)} ${a.appt_type} (${a.salesperson_name || "?"})`).join(" · ")}</p>)}
            {(brief.recommendations || []).length > 0 && (
              <>
                <p className={`${lbl} pt-1`}>Manager recommendations</p>
                <ul className="list-disc ml-4 space-y-0.5">{brief.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul>
              </>)}
          </div>)}
      </div>
      <div className={card} data-testid="weekly-review">
        <div className="flex justify-between items-center mb-2">
          <p className={lbl}>Weekly business review — {weekly?.week || "not generated yet"} (auto Monday 08:00)</p>
          <button type="button" className={btn} onClick={() => regen("weekly")} disabled={!!busy} data-testid="weekly-regen">
            <RefreshCw size={10} /> {busy === "weekly" ? "…" : "Regenerate"}</button>
        </div>
        {weekly && (
          <div className="text-[11px] text-[#3E2A22] space-y-1.5">
            <p>Leads: {weekly.data?.lead_volume} · Qualified: {weekly.data?.qualified} · Won: {weekly.data?.won} · Conversion: {weekly.data?.conversion_pct}%</p>
            <p>Won/Lost: {weekly.data?.won_lost_analysis?.won ?? weekly.data?.won}/{weekly.data?.won_lost_analysis?.lost ?? "—"} · Concepts generated: {weekly.data?.concepts_generated ?? "—"} · Missed upsells flagged: {weekly.data?.missed_upsells_flagged ?? "—"}</p>
            {(weekly.data?.common_objections || []).length > 0 && (
              <p className="text-[#A6822F]">Objections: {weekly.data.common_objections.slice(0, 5).join("; ")}</p>)}
            {weekly.data?.ai_performance?.reviews > 0 && (
              <p>AI performance: {weekly.data.ai_performance.reviews} reviews · response quality {weekly.data.ai_performance.avg_response_quality} · buying intent {weekly.data.ai_performance.avg_buying_intent}</p>)}
            {weekly.executive_summary && <p className="italic text-[#6E1F2C]">{weekly.executive_summary}</p>}
            {(weekly.common_customer_questions || []).length > 0 && (
              <><p className={lbl}>Common customer questions</p>
                <ul className="list-disc ml-4">{weekly.common_customer_questions.map((q, i) => <li key={i}>{q}</li>)}</ul></>)}
            {(weekly.knowledge_gaps || []).length > 0 && (
              <><p className={lbl}>Knowledge gaps</p>
                <ul className="list-disc ml-4 text-[#A6822F]">{weekly.knowledge_gaps.map((q, i) => <li key={i}>{q}</li>)}</ul></>)}
            {(weekly.suggested_improvements || []).length > 0 && (
              <><p className={lbl}>Suggested improvements</p>
                <ul className="list-disc ml-4">{weekly.suggested_improvements.map((q, i) => <li key={i}>{q}</li>)}</ul></>)}
          </div>)}
      </div>
    </div>
  );
}

export function SandboxTab() {
  const [turns, setTurns] = useState([]);
  const [text, setText] = useState("");
  const [service, setService] = useState("");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const [msg, setMsg] = useState("");
  const [vBrief, setVBrief] = useState("");
  const [vPrompt, setVPrompt] = useState("");

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true); setMsg("");
    try {
      const r = await crmApi.sandboxChat({
        message: text.trim(), transcript: turns, service_category: service || null, location: location || null });
      setTurns((t) => [...t, { role: "customer", text: text.trim() }, { role: "ai", text: r.reply || "(no reply)" }]);
      setLast(r); setText("");
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  const testVisual = async () => {
    setBusy(true); setMsg("");
    try { setVPrompt((await crmApi.sandboxVisualPrompt({ brief: vBrief })).generation_prompt); }
    catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid xl:grid-cols-2 gap-3" data-testid="insights-sandbox">
      <div className={`${card} space-y-2`}>
        <p className={lbl}>Simulated customer chat — runs the LIVE prompt + knowledge pipeline, zero production impact</p>
        <div className="grid grid-cols-2 gap-2">
          <input className={inputCls} placeholder="service (e.g. kitchens)" value={service} onChange={(e) => setService(e.target.value)} data-testid="sandbox-service" />
          <input className={inputCls} placeholder="location (e.g. Dubai Hills)" value={location} onChange={(e) => setLocation(e.target.value)} data-testid="sandbox-location" />
        </div>
        <div className="max-h-[300px] overflow-y-auto space-y-1.5 bg-[#FAF5EC] p-2" data-testid="sandbox-transcript">
          {turns.map((t, i) => (
            <p key={i} className={`text-[11px] ${t.role === "customer" ? "text-[#3E2A22]" : "text-[#6E1F2C]"}`}>
              <b className="uppercase text-[9px]">{t.role}:</b> {t.text}</p>))}
          {turns.length === 0 && <p className="text-[11px] text-[#7A6B5F] italic">Play the customer — test prompts, knowledge, qualification & references safely.</p>}
        </div>
        <div className="flex gap-2">
          <input className={inputCls} placeholder="Type a customer message…" value={text} data-testid="sandbox-input"
            onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
          <button type="button" className={btnSolid} onClick={send} disabled={busy} data-testid="sandbox-send">
            <Send size={11} /> {busy ? "…" : "Send"}</button>
          {turns.length > 0 && <button type="button" className={btn} onClick={() => { setTurns([]); setLast(null); }} data-testid="sandbox-reset">Reset</button>}
        </div>
        {msg && <p className="text-[11px] text-[#A3141A]">{msg}</p>}
        {last && (
          <div className="text-[10px] text-[#3E2A22] space-y-1" data-testid="sandbox-analysis">
            <p className={lbl}>AI analysis (internal)</p>
            <p>score: {last.analysis?.lead_score} · status: {last.analysis?.lead_status} · alert: {String(last.analysis?.alert_needed)} · visual: {String(last.analysis?.visual_request)} · references: {String(last.analysis?.reference_request)}</p>
            {(last.kb_sources || []).length > 0 && <p>Knowledge used: {last.kb_sources.join(", ")}</p>}
            {(last.matched_references || []).length > 0 && <p>References matched: {last.matched_references.map((m) => m.title).join("; ")}</p>}
          </div>)}
      </div>
      <div className={`${card} space-y-2`}>
        <p className={lbl}>Image-prompt preview — see the exact generation prompt (nothing generated, no cost)</p>
        <textarea rows={3} className={inputCls} placeholder="Brief, e.g. luxury majlis wall panels, beige & gold, hidden lighting" value={vBrief} onChange={(e) => setVBrief(e.target.value)} data-testid="sandbox-visual-brief" />
        <button type="button" className={btnSolid} onClick={testVisual} disabled={busy || !vBrief.trim()} data-testid="sandbox-visual-btn">
          <Play size={11} /> Preview generation prompt</button>
        {vPrompt && <p className="text-[10px] text-[#3E2A22] whitespace-pre-wrap bg-[#FAF5EC] p-2" data-testid="sandbox-visual-prompt">{vPrompt}</p>}
      </div>
    </div>
  );
}

export function PerformanceTab() {
  const [d, setD] = useState(null);
  const [fb, setFb] = useState(null);
  const [up, setUp] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    crmApi.aiPerformance().then(setD).catch((e) => setErr(e.message));
    crmApi.feedbackStats().then(setFb).catch(() => {});
    crmApi.upsellReport().then(setUp).catch(() => {});
  }, []);
  if (err) return <p className="text-xs text-[#A3141A]">{err}</p>;
  if (!d) return <p className="text-xs text-[#7A6B5F]">Loading…</p>;
  const Metric = ({ label, value, suffix = "%" }) => (
    <div className={card}>
      <p className="text-xl font-semibold text-[#1C110C]">{value ?? "—"}{value != null ? suffix : ""}</p>
      <p className={lbl}>{label}</p>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="insights-performance">
      <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-2">
        <Metric label="AI reply acceptance" value={d.reply_acceptance_pct} />
        <Metric label="AI reply edited" value={d.reply_edit_pct} />
        <Metric label="AI reply rejected" value={d.reply_rejection_pct} />
        <Metric label="Human takeover" value={d.human_takeover_pct} />
        <Metric label="Knowledge accuracy" value={d.knowledge_accuracy_pct} />
        <Metric label="Duplicates blocked" value={d.duplicate_prevention?.blocked} suffix="" />
        <Metric label="Image recognition proxy" value={d.image_recognition?.accuracy_proxy_pct} />
        <Metric label="Voice transcription proxy" value={d.voice_transcription?.accuracy_proxy_pct} />
        <Metric label="Customer satisfaction proxy" value={d.customer_satisfaction_proxy} suffix="/100" />
        <Metric label="Avg customer response delay" value={d.avg_customer_response_delay_min} suffix=" min" />
      </div>
      <div className="grid xl:grid-cols-2 gap-2">
        <div className={card} data-testid="perf-feedback-tags">
          <p className={`${lbl} mb-2`}>Staff feedback tags ({fb?.total || 0} total)</p>
          {(fb?.by_tag || []).length === 0 && <p className="text-[11px] text-[#7A6B5F] italic">No reply feedback yet — use the flag icon on AI messages in a conversation.</p>}
          <ul className="space-y-0.5">
            {(fb?.by_tag || []).map((t) => <li key={t.tag} className="text-[11px] text-[#3E2A22]">{t.label} — {t.n}</li>)}
          </ul>
        </div>
        <div className={card} data-testid="perf-upsell">
          <p className={`${lbl} mb-2`}>Missed upsell report — {up?.month} · {up?.occurrences || 0} flagged · est. AED {(up?.estimated_missed_value_aed || 0).toLocaleString()}</p>
          {(up?.top_missed_services || []).length === 0 && <p className="text-[11px] text-[#7A6B5F] italic">No missed upsells flagged this month.</p>}
          <ul className="space-y-0.5">
            {(up?.top_missed_services || []).map((s) => <li key={s.service} className="text-[11px] text-[#3E2A22]">{s.service} — {s.n}×</li>)}
          </ul>
          {(up?.top_cross_sell_combos || []).length > 0 && (
            <>
              <p className={`${lbl} mt-2 mb-1`}>Most common cross-sell combos</p>
              <ul className="space-y-0.5">{up.top_cross_sell_combos.map((c) => <li key={c.combo} className="text-[11px] text-[#6E1F2C]">{c.combo} — {c.n}×</li>)}</ul>
            </>)}
        </div>
      </div>
    </div>
  );
}

export function CoachingTab() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { crmApi.coaching().then((x) => setR(x.report)).catch((e) => setMsg(e.message)); }, []);
  const gen = async () => {
    setBusy(true); setMsg("");
    try { setR((await crmApi.coaching(true)).report); } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  const Section = ({ title, items, color = "#3E2A22" }) => (items || []).length > 0 && (
    <div>
      <p className={`${lbl} mb-1`}>{title}</p>
      <ul className="list-disc ml-4 text-[11px] space-y-0.5" style={{ color }}>
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="insights-coaching">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-[11px] text-[#7A6B5F]">Weekly coaching engine — analyses edits, rejections, objections, gaps and misses. Recommendations only: nothing changes without admin approval (auto-generated every Monday).</p>
        <button type="button" className={btnSolid} onClick={gen} disabled={busy} data-testid="coaching-generate">
          <Sparkles size={11} /> {busy ? "Analysing…" : r ? "Regenerate" : "Generate now"}</button>
      </div>
      {msg && <p className="text-[11px] text-[#A3141A]">{msg}</p>}
      {r && (
        <div className={`${card} space-y-2.5`} data-testid="coaching-report">
          <p className="text-[12px] font-medium text-[#1C110C]">Week {r.week} · generated {r.created_at?.slice(0, 16).replace("T", " ")}</p>
          <p className="text-[10px] text-[#A6822F]">{r.approval_note}</p>
          <div className="text-[10px] text-[#7A6B5F]">
            Feedback tags this week: {Object.entries(r.data?.feedback_tags || {}).map(([k, v]) => `${k}:${v}`).join(" · ") || "none"} ·
            Missed upsells: {r.data?.missed_upsells_flagged || 0} · Overdue quotes: {r.data?.overdue_quotations || 0} · Qualified w/o visit: {r.data?.qualified_without_visit || 0}
          </div>
          <Section title="Prompt weaknesses" items={r.prompt_weaknesses} color="#A3141A" />
          <Section title="Knowledge gaps" items={r.knowledge_gaps} color="#A6822F" />
          <Section title="Reference library gaps" items={r.reference_library_gaps} color="#A6822F" />
          <Section title="Staff coaching points" items={r.staff_coaching_points} />
          <Section title="Recommendations (admin decides)" items={r.recommendations} color="#6E1F2C" />
        </div>)}
      {!r && !msg && <p className="text-[11px] text-[#7A6B5F] italic">No coaching report yet — click Generate now.</p>}
    </div>
  );
}
